import type { ParseConfig, ParseResult } from "papaparse";

import type { CsvRecord, CsvRow } from "./papaparse";
import { parse as papaparse } from "./papaparse";

export type { CsvRecord, CsvRow };

// we use this type in a few places where we want the csv content by itself
export type IParsedCSVContent = CsvRecord[];

export function escapeCsvValue(rawValue: any): string | number {
  const value =
    typeof rawValue === "string" ? rawValue : JSON.stringify(rawValue);
  // string values are returned with quotes, escaping all internal quotes
  // a double quote "" is used to escape a quote, as in "this has 'quotes""'
  return /(\r\n|,|")/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
function arrayToCsvString(row: any[], delimiter: string) {
  return (
    row
      .map((value) => escapeCsvValue(value))
      // from wikipedia: some countries like Italy use semicolon as the delimiter
      // this gives us the flexibility we need to support that
      .join(delimiter)
  );
}

/**
 * Utility to turn a string array into a csv string
 */
export function matrixToCsvString(data: any[][], delimiter = ",") {
  return data.map((row) => arrayToCsvString(row, delimiter)).join("\r\n");
}

/**
 *  flips 2d array along the Array[n][n] line. e.x.
 *  [1,2,3]        [1,4,7]
 *  [4,5,6]  --->  [2,5,8]
 *  [7,8,9]        [3,6,9]
 */
export function invertMatrix<T>(array: T[][]): T[][] {
  const result: T[][] = [];
  array.forEach((row, rowIndex) => {
    row.forEach((item, itemIndex) => {
      if (result.length < itemIndex + 1) {
        result.push(Array(rowIndex).fill(undefined));
      }
      result[itemIndex].push(item);
    });
  });

  return result;
}

export async function parse<
  Header extends ParseConfig["header"] = false,
  DynamicTyping extends ParseConfig["dynamicTyping"] = undefined,
>(
  content: File | string,
  config: ParseConfig & { header?: Header; dynamicTyping?: DynamicTyping } = {},
): Promise<
  ParseResult<
    Header extends true ? CsvRecord<DynamicTyping> : CsvRow<DynamicTyping>
  >
> {
  const result = await papaparse(content, config);

  // KAW Some errors are recoverable (like auto detection of delimiters)
  // and we should not fail if there is data available, let the backend

  if (
    result.errors.length !== 0 &&
    (!result.data || result.data.length === 0)
  ) {
    throw new Error("Error parsing CSV");
  }
  return result;
}

export const parseCsvToObject = (
  content: File | string,
  config?: Omit<ParseConfig, "dynamicTyping" | "header">,
) => parse(content, { ...config, dynamicTyping: true, header: true });

export const ROW_NUMBER_HEADER = Symbol("#");

type THeadOrTail =
  | {
      head: number;
      tail?: undefined;
    }
  | {
      head?: undefined;
      tail?: number;
    };

export type TManipulateCsvDataOptions = {
  reverse?: boolean;
  addRowNumbers?: boolean;
} & THeadOrTail;

/**
 *
 * @param csvContent
 * @param options
 * @param options.addRowNumbers - when true, numbers the rows starting from 1
 * @param options.reverse - when true, reverses the row order
 * @param options.head - when specified, returns only the first N rows, cannot
 *                       be specified if `tail` is specified
 * @param options.tail - when specified, returns only the last N rows, cannot
 *                       be specified if `head` is specified
 */
export const manipulateCsvData = (
  csvContent: IParsedCSVContent,
  { addRowNumbers, head, reverse, tail }: TManipulateCsvDataOptions,
) => {
  const numRows = csvContent.length;
  let manipulatedData = head
    ? csvContent.slice(0, head)
    : tail
      ? csvContent.slice(-tail)
      : [...csvContent]; // for consistency create a new array even though unnecessary

  if (addRowNumbers) {
    // If we're tailing the data, and the data is going to be truncated (tailing
    // amount is less than the total number of rows), we will need to offset the
    // row numbers
    const offset = tail && numRows > tail ? numRows - tail : 0;

    manipulatedData = manipulatedData.map((row, rowIdx) => ({
      // +1 so that we start at 1 instead of 0
      [ROW_NUMBER_HEADER]: rowIdx + 1 + offset,
      ...row,
    }));
  }

  if (reverse) {
    manipulatedData.reverse();
  }

  return manipulatedData;
};
